import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/ModeEditRounded";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { Wrapper } from "./styles";
import UserTable from "../../../Member/components/UserTable/UserTable";
import { ControlForm, Modal } from "../../../../components";
import AccountLayout from "../../../Member/components/AccountLayout";
import {
  ICreateDepartmentModel,
  IDepartment
} from "../../../../models/department";
import { TagInput } from "../../../WorkflowManagement/components/TagInput/TagInput";
import { useForm } from "react-hook-form";
import { ITokenPayload, IUser } from "../../../../models/user";
import DepartmentService from "../../../../services/api/department.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import GroupRemoveRoundedIcon from "@mui/icons-material/GroupRemoveRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import CircleIcon from "@mui/icons-material/Circle";
import authTokens from "../../../../services/local/auth-tokens";
import { toggleConfirmModal } from "../../../../components/ConfirmModal/ConfirmModal";
import {
  DepartMentPermissionModel,
  FeatureModel,
  TagType
} from "../../../../models/common/models.enum";
import { useApi } from "../../../../hooks/useApi.hook";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import InputForm from "../../../../components/controls/InputForm";
import RoleService from "../../../../services/api/role.service";
import { IFeaturePermission } from "../../../../models/user/models.roles";
import { theme } from "../../../../theme/theme";
import { useTranslation } from "react-i18next";

const DetailDepartment = () => {
  const params = useParams();
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<ITokenPayload | null>(null);
  const getSingle = useApi<IDepartment>({ isFetch: true });
  const updateDepartment = useApi<IDepartment>({});
  const deleteDepartment = useApi<IDepartment>({});
  const [item, setItem] = useState<IDepartment | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>();
  const [permissions, setPermissions] = useState<string[]>([]);
  const getFeaturePersmission = useApi<IFeaturePermission[]>({ isFetch: true });
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  function fetchData() {
    if (params.id) {
      getSingle.request(DepartmentService.getSingle(params.id));
    }
    setCanEdit(false);
  }

  useEffect(() => {
    if (userId)
      getFeaturePersmission.request(
        RoleService.getFeaturePemissionByUserId(userId)
      );
  }, [userId]);

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserId(user.userId);
    }
    getUserInfo();
    fetchData();
  }, []);

  useEffect(() => {
    if (getFeaturePersmission.data) {
      setPermissions(
        getFeaturePersmission.data.find(
          f => f.feature === FeatureModel.Department
        )?.permissions ?? []
      );
    }
  }, [getFeaturePersmission.data]);

  useEffect(() => {
    setItem(getSingle.data);
    setValue("name", getSingle.data?.name);
    setValue("userName", getSingle.data?.userName);
    // setValue("leaderId", getSingle.data?.leaderId);
  }, [getSingle.data]);

  const validationSchema = Yup.object().shape(
    canEdit
      ? checked
        ? {
            name: Yup.string().required(t("Department.PleaseEnterGroupName")),
            userName: Yup.string().required(
              t("Department.PleaseEnterUsername")
            ),
            leaderId: Yup.array()
              .required(t("Department.PleaseSelectGroupAdmin"))
              .min(1, t("Department.PleaseSelectGroupAdmin"))
          }
        : {
            name: Yup.string().required(t("Department.PleaseEnterGroupName")),
            userName: Yup.string().required(t("Department.PleaseEnterUsername"))
          }
      : {
          memberIds: Yup.array()
            .required(t("Department.PleaseSelectMembers"))
            .min(1, t("Department.PleaseSelectMembers"))
        }
  ) as any;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<any>({
    defaultValues: useMemo(() => item, [item]),
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    if (updateDepartment.success) {
      toggleMessage({
        type: "success",
        message: t("UserDetail.ToastUpdateSuccess")
      });
      setOpenCreate(false);
      setCanEdit(false);
      setChecked(false);
      reset();
      fetchData();
      if (canDelete) {
        setCanDelete(false);
      }
    }
    if (updateDepartment.error) {
      toggleMessage({
        type: "error",
        message: updateDepartment?.error ?? t("UserDetail.ToastErrorDefault")
      });
    }
  }, [updateDepartment.success, updateDepartment.error]);

  useEffect(() => {
    if (deleteDepartment.success) {
      toggleMessage({
        type: "success",
        message: t("Department.DeleteDepartmentSuccessMessage")
      });
      setOpenCreate(false);
      navigate(-1);
    }
    if (deleteDepartment.error) {
      toggleMessage({
        type: "error",
        message: updateDepartment?.error ?? t("UserDetail.ToastErrorDefault")
      });
    }
  }, [deleteDepartment.success, deleteDepartment.error]);

  const onSubmit = (request: ICreateDepartmentModel) => {
    if (item) {
      const old = item?.infoMembers?.map(member => member.id);
      const news = !canEdit ? request.memberIds?.map(member => member.id) : [];
      const memberIds = openCreate ? [...old, ...news] : item.memberIds;
      const name = canEdit ? request.name : item.name;
      const userName = canEdit ? request.userName : item.userName;
      const leaderId =
        canEdit && checked ? request.leaderId[0].id : item.leaderId;
      const req: IDepartment = {
        id: item.id,
        name: name,
        userName: userName,
        leaderId: leaderId,
        memberIds: memberIds,
        infoMembers: [],
        infoLeader: item.infoLeader
      };
      updateDepartment.request(DepartmentService.update(req));
    }
  };

  const onDeleteGroup = () => {
    toggleConfirmModal({
      title: t("Department.ConfirmDeleteDepartmentTitle"),
      content: t("Department.ConfirmDeleteDepartmentContent"),
      onSubmit: async () => {
        if (item) {
          deleteDepartment.request(DepartmentService.deleteGroup(item.id));
        }
      },
      submitText: t("UserDetail.ConfirmDeactivateSubmitText"),
      type: "warning-red"
    });
  };

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserInfo(user);
    }
    getUserInfo();
  }, []);

  const handleDelete = (list: IUser[]) => {
    if (item) {
      const items = list.map(item => item.id);
      const req: IDepartment = {
        id: item.id,
        name: item.name,
        userName: item.userName,
        leaderId: item.leaderId,
        memberIds: items,
        infoMembers: [],
        infoLeader: item.infoLeader
      };
      updateDepartment.request(DepartmentService.update(req));
    }
  };

  const getDisable = (): IUser[] | undefined => {
    if (item?.infoMembers && item?.infoLeader) {
      return [...item?.infoMembers, item?.infoLeader];
    } else {
      return undefined;
    }
  };

  return (
    <Wrapper>
      <AppBar position={matches ? undefined : "static"} color="default">
        <Toolbar>
          <Box className="header-page">
            <Button onClick={() => navigate(-1)}>
              <KeyboardBackspaceRoundedIcon />
            </Button>
            <Box>
              <Typography className="name-text" variant="subtitle1">
                {item?.name}
              </Typography>
              <Typography variant="body2">
                {item?.userName}
                <span>
                  <CircleIcon
                    sx={{
                      width: "4px",
                      height: "4px",
                      margin: "2px 6px",
                      color: "GrayText"
                    }}
                  />
                </span>
                <span>
                  {item?.infoMembers?.length} {t("Department.MembersSuffix")}
                </span>
              </Typography>
            </Box>
          </Box>
          <Box className="admin-action">
            {permissions.includes(DepartMentPermissionModel.Update) && (
              <Button
                variant={matches ? "text" : "outlined"}
                color="info"
                size="small"
                startIcon={<EditIcon />}
                disabled={canDelete}
                onClick={() => {
                  setCanEdit(true);
                  setOpenCreate(true);
                }}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.EditGroupInfoModalTitle")}
                  </Typography>
                )}
              </Button>
            )}
            {permissions.includes(DepartMentPermissionModel.Update) && (
              <Button
                variant={matches ? "text" : "outlined"}
                color="primary"
                size="small"
                startIcon={<GroupAddRoundedIcon />}
                disabled={canDelete}
                onClick={() => setOpenCreate(true)}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.AddMembersButton")}
                  </Typography>
                )}
              </Button>
            )}
            {!!item?.infoMembers.length &&
              permissions.includes(DepartMentPermissionModel.Update) && (
                <Button
                  variant={matches ? "text" : "outlined"}
                  color="error"
                  size="small"
                  startIcon={<GroupRemoveRoundedIcon />}
                  disabled={canDelete}
                  onClick={() => setCanDelete(!canDelete)}
                >
                  {!matches && (
                    <Typography variant="subtitle2">
                      {t("Department.DeleteMembersButton")}
                    </Typography>
                  )}
                </Button>
              )}
            {permissions.includes(DepartMentPermissionModel.Delete) && (
              <Button
                variant={matches ? "text" : "contained"}
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                disabled={canDelete}
                onClick={onDeleteGroup}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.DeleteGroupButton")}
                  </Typography>
                )}
              </Button>
            )}
            <Modal
              title={
                canEdit
                  ? t("Department.EditGroupInfoModalTitle")
                  : t("Department.AddMembersModalTitle")
              }
              textSubmit={t("UserDetail.ModalSubmitText")}
              textClose={t("UserDetail.ModalCancelText")}
              open={openCreate}
              onSubmit={handleSubmit(onSubmit)}
              onClose={() => {
                setCanEdit(false);
                setOpenCreate(false);
              }}
            >
              <form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}
              >
                {canEdit && (
                  <Box>
                    <ControlForm
                      title={t("Department.GroupNameLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("Department.EnterGroupName")}
                        required
                        name={"name"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("Department.UsernameLabel")}
                      isRequired
                      classname="brand-form"
                    >
                      <InputForm
                        placeholder={t("Department.EnterGroupUsername")}
                        required
                        name={"userName"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>

                    <ControlForm
                      title={t("Department.GroupAdminLabel")}
                      classname="brand-form"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        margin="0 4px"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {item?.infoLeader
                            ? item?.infoLeader?.fullName
                            : t("Department.NoGroupAdminYet")}
                        </Typography>
                        <Typography variant="body2" color="GrayText">
                          {t("Department.EditLabel")}
                        </Typography>
                        <Checkbox
                          title="d"
                          checked={checked}
                          onChange={_ => setChecked(!checked)}
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                        />
                      </Box>
                      {checked && (
                        <TagInput
                          name={"leaderId"}
                          control={control}
                          errors={errors}
                          isError={!!errors.leaderId}
                          placeholder={t(
                            "Department.SearchNewGroupAdminPlaceholder"
                          )}
                          limit={1}
                          type={TagType.User}
                        />
                      )}
                    </ControlForm>
                  </Box>
                )}
                {openCreate && !canEdit && (
                  <ControlForm
                    title={t("Department.MembersLabel")}
                    infor={t("Department.AddMultipleMembersInfo")}
                    classname="brand-form"
                  >
                    <TagInput
                      name={"memberIds"}
                      control={control}
                      errors={errors}
                      isError={!!errors.memberIds}
                      placeholder={t("Department.AddMembersPlaceholder")}
                      optionDisabled={getDisable()}
                      type={TagType.User}
                    />
                  </ControlForm>
                )}
              </form>
            </Modal>
          </Box>
        </Toolbar>
      </AppBar>

      <Box className="admin-container">
        <Box>
          <Typography className="name-text" variant="overline">
            {t("Department.AdminLabelUppercase")}
          </Typography>
          {item?.infoLeader ? (
            <Box marginTop="8px">
              <AccountLayout
                name={item?.infoLeader?.fullName ?? ""}
                tag={item?.infoLeader!?.userName ?? ""}
                position={item?.infoLeader!?.position ?? ""}
                avatar={item?.infoLeader?.avatar}
              />
            </Box>
          ) : (
            <Typography variant="body2" marginTop="8px">
              {t("Department.NoAdminYet")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box className="main-container">
        {item && (
          <UserTable
            data={item?.infoMembers}
            canEdit={canDelete}
            cancel={() => setCanDelete(!canDelete)}
            deleted={handleDelete}
          />
        )}
      </Box>
    </Wrapper>
  );
};

export default DetailDepartment;
